import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, ChevronRight, ChevronLeft, Heart, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EventDetailsModal } from '@/components/modals/EventDetailsModal';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader,
  WhatsAppBanner, NoireBanner 
} from '@/components/layout/MobileLayout';
import { getEvents } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { DataSourceIndicator } from '@/components/DataSourceIndicator';
import { getDataSource } from '@/lib/api/config';

// Event Card
const EventCard = ({ event, isPast, onClick }) => (
  <div 
    className="group cursor-pointer"
    onClick={() => onClick(event.id)}
    data-testid={`event-card-${event.id}`}
  >
    <Card className="overflow-hidden bg-card border-border hover:border-primary/50 transition-all duration-300 hover:scale-[1.02]">
      <div className="relative aspect-[4/3]">
        <img 
          src={event.image} 
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Wishlist button */}
        <button 
          className="absolute top-3 right-3 p-2 bg-black/50 rounded-full hover:bg-primary transition-colors z-10"
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`wishlist-btn-${event.id}`}
        >
          <Heart className="w-4 h-4 text-white" />
        </button>

        {/* Live badge */}
        {!isPast && event.livestreamEnabled && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-green-500/90 px-2.5 py-1 rounded-full">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-xs font-semibold text-white">LIVE</span>
          </div>
        )}
        
        {isPast && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-primary-foreground ml-1" fill="currentColor" />
            </div>
          </div>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-lg font-semibold text-white mb-1 line-clamp-1">{event.title}</h3>
          <p className="text-sm text-gray-300">{formatDate(event.date)}</p>
          {!isPast && (
            <Button 
              className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 font-medium"
              onClick={(e) => { e.stopPropagation(); onClick(event.id); }}
            >
              View Event
            </Button>
          )}
        </div>
      </div>
    </Card>
  </div>
);

// Event Section with horizontal scroll
const EventSection = ({ title, events, isPast, onEventClick }) => {
  const scrollRef = React.useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl md:text-2xl font-bold text-white">{title}</h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => scroll('left')}
            className="p-2 rounded-full bg-secondary/80 hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="p-2 rounded-full bg-secondary/80 hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div 
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {events.map(event => (
          <div key={event.id} className="flex-shrink-0 w-72 md:w-80" style={{ scrollSnapAlign: 'start' }}>
            <EventCard event={event} isPast={isPast} onClick={onEventClick} />
          </div>
        ))}
      </div>
    </section>
  );
};

// Main Component
export function UserEventList() {
  const navigate = useNavigate();
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current source from URL for re-fetch on change
  const searchParams = new URLSearchParams(window.location.search);
  const currentSource = searchParams.get('source') || 'mock';

  // Fetch events on mount and when source changes
  useEffect(() => {
    async function fetchEvents() {
      try {
        setLoading(true);
        setError(null);
        console.log(`[UserEventList] Fetching events (source=${currentSource})...`);
        
        const response = await getEvents();
        console.log('[UserEventList] Response:', response);
        
        if (response.success && response.data) {
          setEvents(response.data);
        } else if (response._failed) {
          // API failed - show error, DO NOT use mock data
          console.error('[UserEventList] API FAILED:', response.error);
          setError(response.error?.message || 'Failed to load events from API');
          setEvents([]); // Empty, not mock
        } else {
          console.error('[UserEventList] API returned no data');
          setError('No events returned from API');
          setEvents([]);
        }
      } catch (err) {
        console.error('[UserEventList] Error:', err);
        setError(err.message);
        setEvents([]); // Empty on error, not mock
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, [currentSource]);

  const pastEvents = events.filter(e => e.status === 'past');
  const upcomingEvents = events.filter(e => e.status === 'upcoming');

  const handleEventClick = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (event?.status === 'past' && event?.hasRecap) {
      navigate(`/events/${eventId}/recap${window.location.search}`);
    } else {
      setSelectedEventId(eventId);
      setIsModalOpen(true);
      // Preserve the source query parameter when updating URL
      const searchParams = window.location.search;
      window.history.pushState({}, '', `/events/${eventId}${searchParams}`);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEventId(null);
    // Preserve the source query parameter when going back
    const searchParams = window.location.search;
    window.history.pushState({}, '', `/${searchParams}`);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="user-event-list">
      {/* Data Source Indicator - Shows API URL, Source, Event Count */}
      <DataSourceIndicator 
        eventCount={events.length} 
        loading={loading} 
        error={error}
      />
      
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />

      {/* Loading State */}
      {loading && (
        <div className="fixed inset-0 z-30 bg-background/80 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading events...</p>
          </div>
        </div>
      )}
      
      {/* Mobile Content */}
      <main className="lg:hidden pt-14 pb-24 px-4">
        {/* WhatsApp banner at top - Mobile */}
        <div className="mt-4 mb-6">
          <WhatsAppBanner />
        </div>
        
        {/* Event Sections */}
        {pastEvents.length > 0 && (
          <EventSection 
            title="Past Events" 
            events={pastEvents} 
            isPast={true} 
            onEventClick={handleEventClick}
          />
        )}
        
        {upcomingEvents.length > 0 && (
          <EventSection 
            title="Upcoming Events" 
            events={upcomingEvents} 
            isPast={false}
            onEventClick={handleEventClick}
          />
        )}

        {/* Empty State */}
        {!loading && !error && events.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">No events found</p>
          </div>
        )}

        {/* Noire banner at bottom - Mobile */}
        <div className="mt-6">
          <NoireBanner />
        </div>
      </main>

      {/* Desktop Content */}
      <main className="hidden lg:block pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* WhatsApp banner at top - Desktop (FULL WIDTH) */}
          <div className="mb-8">
            <WhatsAppBanner />
          </div>
          
          {/* Event Sections */}
          {pastEvents.length > 0 && (
            <EventSection 
              title="Past Events" 
              events={pastEvents} 
              isPast={true} 
              onEventClick={handleEventClick}
            />
          )}
          
          {upcomingEvents.length > 0 && (
            <EventSection 
              title="Upcoming Events" 
              events={upcomingEvents} 
              isPast={false}
              onEventClick={handleEventClick}
            />
          )}

          {/* Noire banner at bottom - Desktop */}
          <div className="mt-10">
            <NoireBanner />
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* Event Details Modal */}
      <EventDetailsModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        eventId={selectedEventId}
        event={events.find(e => e.id === selectedEventId)}
      />
    </div>
  );
}

export default UserEventList;
